import React, { useCallback, useEffect, useRef, useState } from "react";

interface ScrollbarProps {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

const Scrollbar: React.FC<ScrollbarProps> = ({
  children,
  className,
  style,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [thumbTop, setThumbTop] = useState(0);
  const [thumbHeight, setThumbHeight] = useState(0);
  const [isScrollable, setIsScrollable] = useState(false);

  const handleScroll = useCallback(() => {
    if (!containerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;

    // Check if scrolling is needed.
    if (scrollHeight <= clientHeight) {
      setIsScrollable(false);
      setThumbTop(0);
      setThumbHeight(0);
      return;
    } else {
      setIsScrollable(true);
    }

    // Calculate the thumb’s size and position.
    const trackHeight = clientHeight;
    const visibleRatio = clientHeight / scrollHeight;
    const thumbSize = trackHeight * visibleRatio;
    const maxThumbTop = trackHeight - thumbSize;
    const scrollRatio = scrollTop / (scrollHeight - clientHeight);
    const thumbOffset = scrollRatio * maxThumbTop;

    setThumbHeight(thumbSize);
    setThumbTop(thumbOffset);
  }, []);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    // Observe size changes on the container (including its children).
    const resizeObserver = new ResizeObserver(() => {
      handleScroll();
    });
    resizeObserver.observe(node);

    // Listen for the scroll event.
    node.addEventListener("scroll", handleScroll);

    // Initialize scrollbar on mount.
    handleScroll();

    return () => {
      resizeObserver.disconnect();
      node.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  return (
    <div
      className={`relative w-full h-full overflow-hidden ${className ?? ""}`}
      style={style}
    >
      <div
        ref={containerRef}
        className="h-full w-full overflow-y-auto scrollbar-none"
      >
        {children}
      </div>

      {isScrollable && (
        <div className="pointer-events-none absolute top-0 right-0 w-2 bg-gray-200 h-full">
          <div
            className="bg-gray-600 rounded"
            style={{
              position: "absolute",
              top: thumbTop,
              height: thumbHeight,
              width: "100%",
            }}
          />
        </div>
      )}
    </div>
  );
};

export default Scrollbar;
